/**
 * Service settings - the Service module's business rules, for the System
 * Administrator. 29-09-2026, migration 0184.
 *
 * The two rules here have lived in service_settings since 0146 and 0163, each
 * changed on its own screen (Advances, Reverse Charge). Those screens are
 * untouched and still work; this page puts both in one place, asks for a
 * reason, and shows every change - including ones made on those screens,
 * which fn_log_setting_change records as "changed on another screen".
 *
 * Reads service_settings (this module's own table) and document_audit (shared
 * plumbing) and nothing else: the company-wide and Materials rules are on the
 * Materials side's System Settings page, linked at the top.
 */
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { ArrowLeft, History, Save, SlidersHorizontal } from 'lucide-react'
import {
  Alert, Card, EmptyState, Field, Input, Loading, Modal, Select, Textarea, useToast,
} from '@/components/ui'
import { useAuth } from '@/lib/auth'
import { usePeople, useQuery } from '@/lib/data'
import { supabase } from '@/lib/supabase'
import { dateTime, rupees } from '@/lib/format'

interface Def {
  key: string
  label: string
  kind: 'rupees' | 'bool' | 'pct' | 'months' | 'days'
  help: string
  onLabel?: string
  offLabel?: string
  /** 0212: the range rpc_set_service_setting enforces, shown beside the box. */
  min?: number
  max?: number
}

const DEFS: Def[] = [
  { key: 'advance_bg_threshold', label: 'Bank guarantee required for a mobilisation advance at or above',
    kind: 'rupees',
    help: 'Below this an advance may be paid without a guarantee. Fixed onto each advance when it is raised, so a change affects advances raised from now on. Also on Advances.' },
  { key: 'rcm_by_notified_category', label: 'Reverse charge decided by the notified category of service',
    kind: 'bool', onLabel: 'On — by the category of service', offLabel: 'Off — as before',
    help: 'Moves the tax treatment on every works bill raised from now on. Also on Reverse Charge.' },

  // Phase 2, 15-10-2026, migration 0212. Starting values apply to NEW work
  // orders only - every saved order keeps its own. The overdue line shows at once.
  { key: 'wo_retention_default_pct', label: 'A new work order starts with retention of',
    kind: 'pct', min: 0, max: 100,
    help: 'What the retention box holds when a NEW work order is opened. The person may still change it; saved orders keep their own.' },
  { key: 'wo_dlp_default_months', label: 'A new work order starts with a defect liability period of',
    kind: 'months', min: 0, max: 120,
    help: 'What the defect liability box holds when a NEW work order is opened. Saved orders keep their own.' },
  { key: 'wo_advance_recovery_default_pct', label: 'A new work order starts with advance recovery per bill of',
    kind: 'pct', min: 0, max: 100,
    help: 'What the advance recovery box holds when a NEW work order is opened. Saved orders keep their own.' },
  { key: 'contractor_overdue_days', label: 'A contractor payable counts as overdue after',
    kind: 'days', min: 1, max: 365,
    help: 'Drives the "Over N days" card, the red highlighting and the warning on Contractor Payables and on each payable.' },
]

const LABEL: Record<string, string> = Object.fromEntries(DEFS.map((d) => [d.key, d.label]))
const show = (d: Def, v: unknown) =>
  v === null || v === undefined || v === '' ? '—'
    : d.kind === 'bool' ? (String(v).toLowerCase() === 'true' ? 'On' : 'Off')
      : d.kind === 'pct' ? `${Number(v)}%`
        : d.kind === 'months' ? `${Number(v)} months`
          : d.kind === 'days' ? `${Number(v)} days`
            : rupees(Number(v))

export default function ServiceSettings() {
  const { profile } = useAuth()
  const isAdmin = profile?.role === 'admin'

  const { data: rows, loading, refresh } = useQuery<any>('service_settings_page',
    () => supabase.from('service_settings').select('key, value, description, updated_at'),
    [], { cacheable: false, idField: 'key' })
  const { data: history, loading: hl, refresh: refreshHistory } = useQuery<any>('service_settings_history',
    () => supabase.from('document_audit')
      .select('id, doc_id, from_status, to_status, actor_email, comments, created_at')
      .eq('doc_type', 'SERVICE_SETTING').order('created_at', { ascending: false }).limit(200),
    [], { cacheable: false })
  const people = usePeople()

  const saved: Record<string, string> = Object.fromEntries(rows.map((r: any) => [r.key, String(r.value ?? '')]))
  const [draft, setDraft] = useState<Record<string, string>>({})
  const [asking, setAsking] = useState<Def | null>(null)

  useEffect(() => {
    setDraft(Object.fromEntries(rows.map((r: any) => [r.key, String(r.value ?? '')])))
  }, [rows])

  if (!isAdmin) {
    return (
      <EmptyState title="Service settings are for the System Administrator"
                  message="These rules are maintained by the System Administrator. Ask them if one needs to change." />
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-xl font-bold text-slate-900">
            <SlidersHorizontal className="h-5 w-5 text-oval-700" /> Service settings
          </h1>
          <p className="text-sm text-slate-500">
            The Service Procurement rules. Only the System Administrator may change them; every
            change needs a reason and is kept below.
          </p>
        </div>
        <Link to="/system-settings" className="btn-secondary btn-sm">
          <ArrowLeft className="mr-1 h-4 w-4" /> System settings
        </Link>
      </div>

      <Alert tone="info" title="Company-wide rules are on the other page">
        Back-dating, month closing and negative stock apply to both modules and are on System
        Settings, with the Materials rules.
      </Alert>

      {loading && rows.length === 0 && <Loading label="Reading the settings…" />}

      {rows.length > 0 && (
        <Card title="Service Procurement">
          <div className="divide-y divide-slate-100">
            {DEFS.filter((d) => d.key in saved).map((d) => {
              const val = draft[d.key] ?? saved[d.key]
              const changed = val !== saved[d.key]
              return (
                <div key={d.key} className="grid gap-3 py-3 lg:grid-cols-[1fr_280px_auto] lg:items-center">
                  <div>
                    <p className="text-sm font-semibold text-slate-800">{d.label}</p>
                    <p className="mt-0.5 text-xs text-slate-500">{d.help}</p>
                    <p className="mt-1 text-[11px] text-slate-400">
                      Now: <strong className="text-slate-600">{show(d, saved[d.key])}</strong>
                      {d.min !== undefined && d.max !== undefined && (
                        <> · allowed {d.min}–{d.max}{d.kind === 'pct' ? '%' : d.kind === 'months' ? ' months' : ' days'}</>
                      )}
                    </p>
                  </div>
                  <div>
                    {d.kind === 'bool' ? (
                      <Select value={val.toLowerCase()} onChange={(e) => setDraft({ ...draft, [d.key]: e.target.value })}
                              options={[
                                { value: 'true', label: d.onLabel ?? 'On' },
                                { value: 'false', label: d.offLabel ?? 'Off' },
                              ]} />
                    ) : (
                      <Input type="number" step={d.kind === 'pct' ? '0.01' : '1'}
                             min={d.min ?? 0} max={d.max} value={val}
                             onChange={(e) => setDraft({ ...draft, [d.key]: e.target.value })} />
                    )}
                  </div>
                  <div className="flex gap-2">
                    <button className="btn-primary btn-sm" disabled={!changed} onClick={() => setAsking(d)}>
                      <Save className="mr-1 h-4 w-4" /> Save
                    </button>
                    {changed && (
                      <button className="btn-ghost btn-sm" onClick={() => setDraft({ ...draft, [d.key]: saved[d.key] })}>
                        Undo
                      </button>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        </Card>
      )}

      <Card title="Change history" subtitle="Every change to these settings, from any screen, newest first"
            actions={<History className="h-4 w-4 text-slate-400" />}>
        {hl && <Loading />}
        {!hl && history.length === 0 && (
          <EmptyState title="No changes yet" message="Every setting is still at the value it started with." />
        )}
        {history.length > 0 && (
          <div className="table-wrap">
            <table className="tbl">
              <thead>
                <tr><th>When</th><th>Setting</th><th>From</th><th>To</th><th>By</th><th>Why</th></tr>
              </thead>
              <tbody>
                {history.map((h: any) => (
                  <tr key={h.id}>
                    <td className="whitespace-nowrap text-xs">{dateTime(h.created_at)}</td>
                    <td className="text-sm">{LABEL[h.doc_id] ?? h.doc_id}</td>
                    <td className="text-xs text-slate-500">{h.from_status ?? '—'}</td>
                    <td className="text-xs font-semibold">{h.to_status ?? '—'}</td>
                    <td className="text-xs">{h.actor_email ? people.name({ email: h.actor_email }) : 'System'}</td>
                    <td className="max-w-[360px] text-xs text-slate-600">{h.comments}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {asking && (
        <ReasonModal def={asking} from={show(asking, saved[asking.key])} to={show(asking, draft[asking.key])}
                     value={draft[asking.key]} onClose={() => setAsking(null)}
                     onSaved={() => { setAsking(null); refresh(); refreshHistory() }} />
      )}
    </div>
  )
}

function ReasonModal({ def, from, to, value, onClose, onSaved }: {
  def: Def; from: string; to: string; value: string
  onClose: () => void; onSaved: () => void
}) {
  const toast = useToast()
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)

  async function save() {
    setBusy(true)
    const { error } = await supabase.rpc('rpc_set_service_setting', {
      p_key: def.key, p_value: String(value).trim(), p_reason: reason.trim(),
    })
    setBusy(false)
    if (error) { toast.error(error.message); return }
    toast.success(`${def.label}: now ${to}.`)
    onSaved()
  }

  return (
    <Modal open onClose={onClose} title="Change a Service setting" subtitle={def.label}
           footer={<>
             <button className="btn-secondary" onClick={onClose} disabled={busy}>Cancel</button>
             <button className="btn-primary" onClick={() => void save()}
                     disabled={busy || reason.trim().length < 10}>
               {busy ? 'Saving…' : 'Save the change'}
             </button>
           </>}>
      <p className="text-sm">From <strong>{from}</strong> to <strong>{to}</strong>.</p>
      <div className="mt-3">
        <Field label="Why is it changing?" required
               hint="At least a short sentence. Kept permanently in the change history — name who decided it.">
          <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} />
        </Field>
      </div>
    </Modal>
  )
}
